import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { HandHelping, Loader2, Stethoscope } from "lucide-react";
import { fmtDate } from "@/lib/dates";

function TroubleshootPanel() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setOpen(true); setLoading(true);
    try { const { data } = await api.get("/open-requests/diagnostics"); setData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not run diagnostics"); }
    finally { setLoading(false); }
  };

  return (
    <div className="mt-4">
      {!open ? (
        <Button variant="outline" onClick={run} data-testid="troubleshoot-btn">
          <Stethoscope className="w-4 h-4 mr-2" />Why am I not seeing students?
        </Button>
      ) : loading ? (
        <Loader2 className="w-5 h-5 animate-spin inline text-zinc-400" />
      ) : data ? (
        <div className="text-left max-w-2xl mx-auto border rounded-lg p-4 bg-zinc-50" data-testid="troubleshoot-result">
          <p className="font-medium text-zinc-800 mb-3">{data.reason}</p>
          {data.ok && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3 text-center">
              {[["Your facility state", data.facility_state_normalized || "—"],
                ["Open in your state", data.open_in_your_state],
                ["Already accepted/placed", data.accepted_or_placed],
                ["Open in other states", data.open_in_other_states_count]].map(([k, v]) => (
                <div key={k} className="bg-white border rounded-md p-2">
                  <p className="text-lg font-semibold text-zinc-900">{v}</p>
                  <p className="text-[11px] uppercase tracking-wide text-zinc-400">{k}</p>
                </div>
              ))}
            </div>
          )}
          {data.schools_in_your_state && (
            <p className="text-sm text-zinc-600 mb-2">
              <span className="font-medium">Schools in {data.facility_state_normalized}:</span>{" "}
              {data.schools_in_your_state.length ? data.schools_in_your_state.join(", ") : "none — set a school's State to match."}
            </p>
          )}
          {data.open_in_other_states_sample?.length > 0 && (
            <div className="mt-3">
              <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold mb-1">Open requests in other states</p>
              <div className="space-y-1">
                {data.open_in_other_states_sample.map((r, i) => (
                  <div key={i} className="text-xs text-zinc-600 flex flex-wrap gap-x-2">
                    <span className="font-medium text-zinc-800">{r.student_name}</span>
                    <span>· {r.school_name}</span>
                    <span className="text-zinc-400">({(r.request_states || []).join("/") || "no state"})</span>
                    <span className="text-zinc-400">· {r.placement_status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Button variant="ghost" size="sm" className="mt-3" onClick={() => { setOpen(false); setData(null); }}>Close</Button>
        </div>
      ) : null}
    </div>
  );
}

export default function OpenRequests() {
  const [rows, setRows] = useState(null);
  const [claiming, setClaiming] = useState(null);
  const [facility, setFacility] = useState(null);

  const load = () => api.get("/open-requests").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => {
    load();
    api.get("/facilities").then(({ data }) => setFacility(data?.[0] || null)).catch(() => {});
  }, []);

  const claim = async (pr) => {
    setClaiming(pr.id);
    try {
      await api.post(`/placement-requests/${pr.id}/claim`);
      toast.success("Claim submitted — awaiting Campus Coordinator approval");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setClaiming(null);
    }
  };

  return (
    <div>
      <PageHeader title="Open Requests" subtitle="Unplaced students in your state — claim one to bring them to your facility" />
      <Card className="p-0 overflow-hidden">
        {rows === null ? (
          <div className="p-10 text-center text-zinc-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          !facility ? (
            <div className="p-8 text-center" data-testid="no-facility-hint">
              <p className="text-zinc-700 font-medium">Your account isn't linked to a facility yet.</p>
              <p className="text-sm text-zinc-500 mt-1">Ask an admin to set your facility (Users → edit your account → Facility) so you can see and claim students in your area.</p>
            </div>
          ) : !facility.state ? (
            <div className="p-8 text-center" data-testid="no-state-hint">
              <p className="text-zinc-700 font-medium">Your facility ({facility.name}) doesn't have a state set yet.</p>
              <p className="text-sm text-zinc-500 mt-1">Open requests are matched by state — set your facility's state on the Facility Profile page (or ask an admin) to see students in your area.</p>
            </div>
          ) : (
            <div className="p-8 text-center">
              <EmptyState text="No open requests in your state right now." />
              <TroubleshootPanel />
            </div>
          )
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="open-requests-table">
              <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
                <tr>
                  <th className="px-5 py-3 text-left">Student</th>
                  <th className="px-5 py-3 text-left">School</th>
                  <th className="px-5 py-3 text-left">Discipline</th>
                  <th className="px-5 py-3 text-left">Rotation</th>
                  <th className="px-5 py-3 text-left">Requested Start</th>
                  <th className="px-5 py-3 text-left">Status</th>
                  <th className="px-5 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-zinc-50" data-testid={`open-pr-${r.id}`}>
                    <td className="px-5 py-3 font-medium text-zinc-800">{r.student?.name || r.student_name}</td>
                    <td className="px-5 py-3 text-zinc-600">{r.school?.name}</td>
                    <td className="px-5 py-3"><span className="font-mono text-xs px-2 py-0.5 bg-zinc-100 rounded">{r.discipline}</span></td>
                    <td className="px-5 py-3 text-zinc-600">{r.rotation_level || "—"}</td>
                    <td className="px-5 py-3 text-zinc-600 font-mono text-xs">{fmtDate(r.requested_start_date)}</td>
                    <td className="px-5 py-3"><StatusBadge status={r.placement_status} /></td>
                    <td className="px-5 py-3 text-right">
                      <Button size="sm" onClick={() => claim(r)} disabled={claiming === r.id} className="bg-cc-green text-white" data-testid={`claim-${r.id}`}>
                        {claiming === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><HandHelping className="w-4 h-4 mr-1" />Claim</>}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
